/**
 * Utility functions for converting analytics configuration to hierarchical filter options
 * This maintains the generic nature of the analytics framework while providing
 * specific utilities for hierarchical data transformation.
 */

interface HierarchyConfig {
  levels: string[];
  branching?: number[];
  relationships?: Record<string, Record<string, { [key: string]: string[] }>>;
}

interface HierarchicalOption {
  value: string;
  label: string;
  children?: HierarchicalOption[];
  level?: number;
  parentValue?: string;
}

/**
 * Converts analytics hierarchy configuration to hierarchical filter options
 * @param hierarchyConfig - The hierarchy configuration from the analytics JSON
 * @param data - Optional dataset to extract actual values from (if not using relationships)
 * @returns Array of hierarchical options suitable for UnifiedFilterComponent
 */
export function convertHierarchyConfigToFilterOptions(
  hierarchyConfig: HierarchyConfig,
  data?: Array<Record<string, any>>
): HierarchicalOption[] {
  if (!hierarchyConfig.levels || hierarchyConfig.levels.length === 0) {
    return [];
  }

  // If we have explicit relationships, use those
  if (hierarchyConfig.relationships) {
    return buildFromRelationships(hierarchyConfig);
  }

  // Otherwise, build from data
  if (data && data.length > 0) {
    return buildFromData(hierarchyConfig.levels, data);
  }

  return [];
}

/**
 * Build hierarchical options from explicit relationships in config
 */
function buildFromRelationships(hierarchyConfig: HierarchyConfig): HierarchicalOption[] {
  const { levels, relationships } = hierarchyConfig;
  const options: HierarchicalOption[] = [];

  if (!relationships || levels.length < 2) {
    return options;
  }

  const [parentLevel, childLevel] = levels;
  const parentToChildren = relationships[parentLevel];

  if (!parentToChildren) {
    return options;
  }

  Object.entries(parentToChildren).forEach(([parentValue, childData]) => {
    const parentOption: HierarchicalOption = {
      value: parentValue,
      label: formatLabel(parentValue),
      level: 0,
      children: []
    };

    // Extract children for this parent
    if (childData[childLevel] && Array.isArray(childData[childLevel])) {
      parentOption.children = childData[childLevel].map(childValue => ({
        value: childValue,
        label: formatLabel(childValue),
        level: 1,
        parentValue: parentValue
      }));
    }

    options.push(parentOption);
  });

  return options;
}

/**
 * Build hierarchical options from actual data
 */
function buildFromData(levels: string[], data: Array<Record<string, any>>): HierarchicalOption[] {
  if (levels.length < 2) {
    // If only one level, return flat options
    const uniqueValues = [...new Set(data.map(row => row[levels[0]]).filter(v => v != null))];
    return uniqueValues.map(value => ({
      value: String(value),
      label: formatLabel(String(value)),
      level: 0
    }));
  }

  const [parentLevel, childLevel] = levels;
  const hierarchyMap = new Map<string, Set<string>>();

  // Build parent-child relationships from data
  data.forEach(row => {
    const parentValue = row[parentLevel];
    const childValue = row[childLevel];

    if (parentValue != null && childValue != null) {
      const parentKey = String(parentValue);
      const childKey = String(childValue);

      if (!hierarchyMap.has(parentKey)) {
        hierarchyMap.set(parentKey, new Set());
      }
      hierarchyMap.get(parentKey)!.add(childKey);
    }
  });

  // Convert to hierarchical options
  const options: HierarchicalOption[] = [];
  hierarchyMap.forEach((children, parentValue) => {
    const parentOption: HierarchicalOption = {
      value: parentValue,
      label: formatLabel(parentValue),
      level: 0,
      children: Array.from(children).map(childValue => ({
        value: childValue,
        label: formatLabel(childValue),
        level: 1,
        parentValue: parentValue
      }))
    };
    options.push(parentOption);
  });

  return options.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Format a value into a human-readable label
 */
function formatLabel(value: string): string {
  return value
    .replace(/[_-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Create filter options for a specific field from dataset
 * This is useful for non-hierarchical multi-select filters
 */
export function createFilterOptionsFromField(
  data: Array<Record<string, any>>,
  fieldName: string
): Array<{ value: string; label: string }> {
  const uniqueValues = [...new Set(data.map(row => row[fieldName]).filter(v => v != null))];
  
  return uniqueValues
    .map(value => ({
      value: String(value),
      label: formatLabel(String(value))
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Extract available filter fields from analytics configuration
 * This helps identify which fields can be used for filtering
 */
export function extractFilterableFields(config: any): string[] {
  const fields: string[] = [];

  // Extract from mock data schema if available
  if (config.mockDataConfig?.schema?.requiredFields) {
    config.mockDataConfig.schema.requiredFields.forEach((field: any) => {
      if (field.type === 'enum' && field.enumValues) {
        fields.push(field.name);
      }
    });
  }

  // Extract from hierarchy configuration
  if (config.mockDataConfig?.hierarchyConfig?.levels) {
    fields.push(...config.mockDataConfig.hierarchyConfig.levels);
  }

  return [...new Set(fields)];
}

export type { HierarchicalOption, HierarchyConfig };
